import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Tag, Icon } from "@ann/web-components";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { generateAvatarColor } from "../../../../../../../utils/avatarUtils";
import { ActivityLogContainer } from "./ActivityLog.styled";
import { AuditLogTableSection } from "./components";
import { getAuditLog } from "../../../../services";
import { useDebounce } from "../../../../../../../hooks";
import dayjs from "dayjs";
import { UserCellContainer, UserInfoContainer, UserNameText, VisibilityContainer, StatusContainer } from "./ActivityLog.styled";

const TABLE_HEADERS = [
  { label: 'Timestamp', value: 'timestamp' },
  { label: 'User', value: 'user' },
  { label: 'Action', value: 'action' },
  { label: 'Resource', value: 'resource' },
  { label: 'Visibility', value: 'visibility' },
  { label: 'Status', value: 'status' }
];

const ActivityLog = ({ departmentId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("timestamp");
  const [orderDirection, setOrderDirection] = useState("DESC");
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
  });

  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  useEffect(() => {
    const fetchActivityLog = async () => {
      if (!departmentId) return;
      
      setLoading(true);
      try {
        const response = await getAuditLog({
          page: currentPage,
          page_size: itemsPerPage,
          order_by: orderBy,
          order_direction: orderDirection,
          search: debouncedSearchKeyword,
          roles: selectedRoles,
          actions: selectedActions,
          sections: selectedSections,
        });

        if (response && response.body) {
          setActivities(response.body);
          if (response.meta) {
            setMeta({
              totalItems: response.meta.totalItems || 0,
              totalPages: response.meta.totalPages || 0,
              currentPage: response.meta.currentPage || currentPage,
              perPage: response.meta.perPage || itemsPerPage,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching audit log:", error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityLog();
  }, [currentPage, itemsPerPage, orderBy, orderDirection, debouncedSearchKeyword, selectedRoles, selectedActions, selectedSections, departmentId]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((selectedOption) => {
    const validOrderBy = ["timestamp", "userName", "action", "resource"];
    const newOrderBy = validOrderBy.includes(selectedOption) ? selectedOption : "timestamp";
    setOrderBy(newOrderBy);
    setCurrentPage(1);
  }, []);

  const handleOrderChange = useCallback((currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  }, []);

  const handleRoleChange = useCallback((role) => {
    setSelectedRoles(role ? [role] : []);
    setCurrentPage(1);
  }, []);

  const handleActionChange = useCallback((action) => {
    setSelectedActions(action ? [action] : []);
    setCurrentPage(1);
  }, []);

  const handleSectionChange = useCallback((section) => {
    setSelectedSections(section ? [section] : []);
    setCurrentPage(1);
  }, []);

  const formatTimestamp = useCallback((dateString) => {
    if (!dateString) return "—";
    return dayjs(dateString).format("YYYY-MM-DD HH:mm");
  }, []);

  const getRoleColor = useCallback((role) => {
    if (!role) return "neutral";
    const roleLower = role.toLowerCase();
    if (roleLower.includes("platform admin") || roleLower.includes("platform")) {
      return "informative";
    }
    if (roleLower.includes("department admin") || roleLower.includes("department")) {
      return "primary";
    }
    if (roleLower.includes("group manager") || roleLower.includes("manager")) {
      return "success";
    }
    return "neutral";
  }, []);

  const tableData = useMemo(() => {
    return activities.map((log) => {
    const userName = log.userName || log.user_full_name || log.userFullName || "Unknown User";
    const userRole = log.userRole || log.role || "User";
    const avatarColor = generateAvatarColor(userName);
    const actionText = log.action || "—";
    const resource = log.item || log.resource || "—";
    const visibility = log.visibility || "Dept";
    const status = log.status || log.outcome || "Success";

    return {
      id: log.activityUuid || log.id || log.created_at || log.timestamp,
      timestamp: formatTimestamp(log.timestamp || log.created_at || log.createdAt),
      user: (
        <UserCellContainer>
          <NameInitialsAvatar
            name={userName}
            size="32px"
            bgColor={avatarColor}
            textColor="#ffffff"
            borderRadius="50%"
            textWeight="bold"
            textSize="12px"
            borderWidth="0"
          />
          <UserInfoContainer>
            <UserNameText>{userName}</UserNameText>
            <Tag
              color={getRoleColor(userRole)}
              size="small"
              text={userRole}
            />
          </UserInfoContainer>
        </UserCellContainer>
      ),
      action: (
        <Tag
          color="neutral"
          size="small"
          text={actionText}
        />
      ),
      resource,
      visibility: (
        <VisibilityContainer>
          {visibility === "Global" ? (
            <>
              <Icon name="globe" size={16} color="body" />
              <span>Global</span>
            </>
          ) : (
            <Tag
              color="neutral"
              size="small"
              icon="lock"
              text="Dept"
            />
          )}
        </VisibilityContainer>
      ),
      status: (
        <StatusContainer>
          {status === "Success" ? (
            <>
              <span style={{ color: "#10b981" }}>Success</span>
            </>
          ) : status === "Failed" ? (
            <>
              <span style={{ color: "#ef4444" }}>Failed</span>
            </>
          ) : (
            <>
              <Icon name="alert" size={16} color="alert" />
              <span style={{ color: "#f59e0b" }}>{status}</span>
            </>
          )}
        </StatusContainer>
      ),
    };
  });
  }, [activities, formatTimestamp, getRoleColor]);

  const selectedSortOption = useMemo(() => {
    const validOrderBy = ["timestamp", "userName", "action", "resource"];
    return validOrderBy.includes(orderBy) ? orderBy : "timestamp";
  }, [orderBy]);

  const orderDirectionLower = useMemo(() => {
    return orderDirection.toLowerCase();
  }, [orderDirection]);

  return (
    <ActivityLogContainer>
      <AuditLogTableSection
        tableData={tableData}
        headers={TABLE_HEADERS}
        currentPage={meta.currentPage || currentPage}
        itemsPerPage={meta.perPage || itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        totalItems={meta.totalItems || 0}
        totalPages={meta.totalPages || 0}
        loading={loading}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={selectedSortOption}
        orderDirection={orderDirectionLower}
        onSearchChange={handleSearchChange}
        onRoleChange={handleRoleChange}
        onActionChange={handleActionChange}
        onSectionChange={handleSectionChange}
      />
    </ActivityLogContainer>
  );
};

export default ActivityLog;
